import { useCallback, useEffect, useRef, useState } from "react";
import { readChatSession, saveChatSession, loadChatSession, deleteChatSession, streamHeliosChat } from "../lib/helios-chat-client";

const TIMEOUT = Number(process.env.REACT_APP_DEMO_TIMEOUT_MS);
if (!Number.isFinite(TIMEOUT) || TIMEOUT <= 0) throw new Error("Demo request timeout must be configured.");

export function useHeliosChat(open, page) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");
  const [model, setModel] = useState("gpt-5.5");
  const [restoreFailed, setRestoreFailed] = useState(false);
  const session = useRef(undefined), controller = useRef(null), sequence = useRef(0), initialized = useRef(false), lastRequest = useRef(null);
  if (session.current === undefined) session.current = readChatSession();
  const busy = phase === "sending" || phase === "resetting";
  const restoring = phase === "restoring";

  const restore = useCallback(async () => {
    initialized.current = true;
    if (!session.current) return;
    const current = ++sequence.current, abort = new AbortController(); controller.current = abort;
    setPhase("restoring"); setError(""); setRestoreFailed(false);
    const timer = window.setTimeout(() => abort.abort(), TIMEOUT);
    try {
      const saved = await loadChatSession(session.current, abort.signal);
      if (current !== sequence.current) return;
      if (!saved) { session.current = null; saveChatSession(null); setMessages([]); }
      else { setMessages(saved.messages); setModel(saved.model); }
      setPhase("idle");
    } catch (failure) {
      if (current !== sequence.current) return;
      setError("Your conversation could not be restored. Retry or start a new conversation."); setRestoreFailed(true); setPhase("error");
    } finally { window.clearTimeout(timer); if (current === sequence.current) controller.current = null; }
  }, []);
  useEffect(() => { if (open && !initialized.current) restore(); }, [open, restore]);
  useEffect(() => () => { sequence.current += 1; controller.current?.abort(); }, []);
  useEffect(() => {
    if (phase !== "sending") return undefined;
    const warnBeforeLeaving = event => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [phase]);

  const markIncomplete = requestId => setMessages(previous => previous.map(message => message.request_id === requestId ? { ...message, incomplete: true } : message));
  const send = async (input, retryPayload = null) => {
    const text = input.trim();
    if (controller.current || busy || restoring || restoreFailed || !text || text.length > 2000) return;
    if (!session.current) { session.current = crypto.randomUUID(); saveChatSession(session.current); }
    const payload = retryPayload || { session_id: session.current, request_id: crypto.randomUUID(), message: text, page };
    lastRequest.current = payload;
    const current = ++sequence.current, abort = new AbortController(); controller.current = abort;
    setPhase("sending"); setError(""); if (!retryPayload) setDraft("");
    const user = { id: `${payload.request_id}-user`, request_id: payload.request_id, role: "user", content: text };
    setMessages(previous => [...previous.filter(message => message.request_id !== payload.request_id), user]);
    let output = "", timedOut = false;
    const timer = window.setTimeout(() => { timedOut = true; abort.abort(); }, TIMEOUT);
    try {
      const complete = await streamHeliosChat(payload, abort.signal, delta => {
        if (current !== sequence.current) return;
        output += delta;
        const assistant = { id: `${payload.request_id}-assistant`, request_id: payload.request_id, role: "assistant", content: output };
        setMessages(previous => [...previous.filter(message => message.id !== assistant.id), assistant]);
      }, status => { if (current === sequence.current && status.model) setModel(status.model); });
      if (current !== sequence.current) return;
      if (!complete.ai_generated || !complete.assistant?.content) throw new Error("No complete AI reply was received.");
      setMessages(previous => [...previous.filter(message => message.request_id !== payload.request_id), complete.user, complete.assistant]);
      setModel(complete.model); setPhase("idle"); lastRequest.current = null;
    } catch (failure) {
      if (current !== sequence.current) return;
      markIncomplete(payload.request_id); setPhase("error");
      setError(timedOut ? "Helios could not finish in time. Retry your message." : failure instanceof TypeError ? "The connection was interrupted. Retry your message." : failure.message);
    } finally { window.clearTimeout(timer); if (current === sequence.current) controller.current = null; }
  };
  const stop = () => {
    sequence.current += 1; controller.current?.abort(); controller.current = null;
    if (lastRequest.current) markIncomplete(lastRequest.current.request_id);
    setPhase("stopped"); setError("Reply stopped. Unfinished exchanges are not saved; retry to complete this one.");
  };
  const reset = async () => {
    sequence.current += 1; controller.current?.abort(); controller.current = null;
    const current = sequence.current;
    setPhase("resetting"); setError("");
    try {
      if (session.current) await deleteChatSession(session.current);
      if (current !== sequence.current) return;
      session.current = null; saveChatSession(null); lastRequest.current = null; initialized.current = true;
      setMessages([]); setDraft(""); setRestoreFailed(false); setPhase("idle");
    } catch { if (current === sequence.current) { setPhase("error"); setError("The saved conversation could not be cleared. Please retry New conversation."); } }
  };
  return { messages, draft, setDraft, phase, busy, restoring, restoreFailed, error, model, send, stop, reset, restore,
    canRetry: Boolean(lastRequest.current), retry: () => { const last = lastRequest.current; if (last) send(last.message, last); } };
}